import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircleIcon, WarningCircleIcon, InfoIcon, XCircleIcon } from '@phosphor-icons/react/ssr'
import { Badge } from '@/components/ui/badge'

type StatusType = 'success' | 'error' | 'warning' | 'info'
type SizeType = 'small' | 'large'

type FeedbackProps = {
  status?: StatusType
  message: React.ReactNode
  className?: string
  size?: SizeType
  toast?: boolean
} & React.ComponentPropsWithoutRef<'div'>

const statusStyles: Record<StatusType, string> = {
  success: 'bg-green-50 text-green-700 dark:bg-green-900/10 dark:text-green-400',
  error: 'bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-400',
  warning: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/10 dark:text-yellow-400',
  info: 'bg-blue-50 text-blue-700 dark:bg-blue-900/10 dark:text-blue-400',
}

// Badge palette for the compact variant (shadcn Badge has no `color` prop).
const statusBadgeStyles: Record<StatusType, string> = {
  success: 'border-transparent bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  error: 'border-transparent bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  warning: 'border-transparent bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
  info: 'border-transparent bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
}

const statusIcons: Record<StatusType, React.ElementType> = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: WarningCircleIcon,
  info: InfoIcon,
}

export function Feedback({
  status = 'info',
  message,
  className,
  size = 'large',
  toast = false,
  ...props
}: FeedbackProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const Icon = statusIcons[status]

  if (toast) {
    if (!mounted) return null
    return createPortal(
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] w-full max-w-md px-4 pointer-events-none animate-in fade-in slide-in-from-top-4 duration-300">
        <div
          className={clsx(
            'pointer-events-auto rounded-lg shadow-lg border border-zinc-200/50 dark:border-zinc-700/50 p-4 backdrop-blur-md bg-white/95 dark:bg-zinc-900/95',
            statusStyles[status],
            className
          )}
          {...props}
        >
          <div className="flex">
            <div className="shrink-0">
              <Icon
                aria-hidden="true"
                className={clsx("size-5", {
                  "text-green-500": status === "success",
                  "text-red-500": status === "error",
                  "text-yellow-500": status === "warning",
                  "text-blue-500": status === "info",
                })}
                weight="fill"
              />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{message}</p>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  if (size === 'small') {
    return (
      <Badge className={clsx('gap-1 px-3 mb-2', statusBadgeStyles[status], className)}>
        <Icon aria-hidden="true" className="size-3.5" weight="fill" />
        <span>{message}</span>
      </Badge>
    )
  }

  return (
    <div
      className={clsx(
        'rounded-md p-4 mb-4',
        statusStyles[status],
        className
      )}
      {...props}
    >
      <div className="flex">
        <div className="shrink-0">
          <Icon
            aria-hidden="true"
            className={clsx("size-5", {
              "text-green-500": status === "success",
              "text-red-500": status === "error",
              "text-yellow-500": status === "warning",
              "text-blue-500": status === "info",
            })}
            weight="fill"
          />
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium">{message}</p>
        </div>
      </div>
    </div>
  )
}

